import { ChangeDetectorRef } from '@angular/core';
import { FormlyFieldConfig } from '@ngx-formly/core';
import { Subject } from 'rxjs';

export interface JourneyModel {
  serviceSequence: ServiceDefinition[];
  triggerParentDefinition: TriggerParentDefinition[];
}

export interface ServiceDefinition {
  serviceId: string;
  serviceName: string;
  changeRequestId: string;
  serviceCode: string;
  serviceSource?: string;
  order: number;
  required: boolean;
  repeatingFieldsMapping?: RepeatingFieldMapping[];
  attachmentsMapping?: AttachmentsMapping;
  stateMapping?: StateMapping;
}

export interface TriggerParentDefinition {
  serviceCode: string;
  triggerParentStates: string[];
}

export interface RepeatingFieldMapping {
  targetField: string;
  sourceService: string;
  sourceField: string;
}

export interface AttachmentsMapping {
  officerAttachment?: OfficerAttachmentMapping[];
  certificates?: CertificateMapping[];
}

export interface OfficerAttachmentMapping {
  targetField: string;
  sourceService: string;
  sourceField: string;
}

export interface CertificateMapping {
  language: string;
  targetField: string;
  sourceService: string;
  certificateCode: string;
}

export interface StateMapping {
  approve: string;
  reject: string;
}

export interface ServiceOption {
  id: string;
  serviceName: string;
  serviceCode: string;
  changeRequestId?: string;
  formFields?: FieldData[];
  workflowStates?: string[];
}

export interface FieldData {
  fieldName: string;
  fieldLabel: string;
}

export interface FieldOption {
  label: string;
  value: string;
}

export interface FieldUpdateContext {
  field: FormlyFieldConfig;
  cdr: ChangeDetectorRef;
  destroy$: Subject<void>;
}

export interface ServiceModel {
  serviceName: string;
  changeRequestId: string;
  serviceId: string;
  serviceCode: string;
  order: number;
}

export interface FieldPathConfig {
  modelPath: string;
  fieldGroupPath: string;
}

export const DEFAULT_FIELD_PATH: FieldPathConfig = {
  modelPath: 'parent?.parent?.parent?.parent?.model',
  fieldGroupPath: 'parent?.parent?.parent?.parent?.fieldGroup',
};

export const ATTACHMENT_FIELD_PATH: FieldPathConfig = {
  modelPath: 'parent?.parent?.parent?.parent?.parent?.model',
  fieldGroupPath: 'parent?.parent?.parent?.parent?.parent?.fieldGroup',
};

/**
 * Journey V3 Model - matches backend JourneyMappingDto
 */
export interface JourneyV3Model {
  serviceSequence?: ServiceDefinition[];
  triggerParentDefinition?: TriggerParentDefinition[]; // V2 backward compatibility
  triggerParentMappings?: TriggerParentMapping[]; // V3 flexible mappings
  additionalApplicationSettings?: AdditionalApplicationSetting; // V3 settings
}

/**
 * Flexible trigger parent mapping (Journey V3)
 * Maps child service state to parent event
 */
export interface TriggerParentMapping {
  childServiceCode: string;
  childState: string;
  parentEvent: string;
}

/**
 * Additional application settings
 * Controls application behavior and visibility
 */
export interface AdditionalApplicationSetting {
  hideFromUserList?: boolean;
  rfaOnParent?: boolean;
  configurations?: Record<string, any>; // Extensibility within settings
}

/**
 * Complete journey mapping (supports V2 and V3)
 */
export interface CompleteJourneyMapping {
  // Common fields
  serviceSequence?: ServiceDefinition[];

  // V2 fields
  triggerParentDefinition?: TriggerParentDefinition[];

  // V3 fields
  triggerParentMappings?: TriggerParentMapping[];
  additionalApplicationSettings?: AdditionalApplicationSetting;
}
